import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { fetchFundamentals as fetchFundamentalsAction } from '../../../actions/market';
import { start, stop } from '../../../actions/stream';
import { selectChannel } from '../../../selectors/channelSelectors';
import { computeMarketCap, computeVwap, getFundamentalValue } from '../../../utils/fundamentals';
import useActions from '../../../utils/hooks/useActions';
import useLocalize from '../../../utils/hooks/useLocalize';
import { getSymbolClose } from '../../../utils/symbol';
import CssFlash from '../../common/CssFlash';
import Number from '../../common/Number';
import './MarketCapSection.scss';
import './SummarySection.scss';

const MarketCapSection = ({ symbol }) => {
  // Actions
  const [startStream] = useActions([start], []);
  const [stopStream] = useActions([stop], []);
  const [fetchFundamentals] = useActions([fetchFundamentalsAction], []);

  // Intl
  const localize = useLocalize();

  // Redux - quote
  const channel = useMemo(() => `${symbol}/quote`, [symbol]);
  const quote = useSelector((state) => selectChannel(state, channel));

  // Redux - metadata
  const metadata = useSelector((state) => state.symbols.data || {});
  const symbolMetadata = (metadata[symbol] || {}).data || {};
  const precision = symbolMetadata.precision || 0;
  const close = getSymbolClose(symbolMetadata.assetClass, quote);

  // Redux - fundamentals
  const allFundamentals = useSelector(
    (state) => (state.market || {}).fundamentals || {}
  );
  const fundamentals =
    ((allFundamentals[symbol] || {}).data || {}).fundamentals || {};
  const sharesOutstanding = getFundamentalValue(fundamentals, 'sharesOutstanding', 'value');

  useEffect(() => {
    startStream(`${symbol}/quote`);
    fetchFundamentals(symbol);

    return () => {
      stopStream(`${symbol}/quote`);
    };
  }, [symbol, startStream, stopStream, fetchFundamentals]);

  const sharesOutstandingValue = sharesOutstanding || 0;
  const marketCapValue = computeMarketCap(sharesOutstanding, close);
  const rawVwap = computeVwap(quote.turnover, quote.volume);
  const vwapValue = parseFloat(rawVwap.toFixed(precision));

  const rows = [
    {
      key: 'sharesOutstanding',
      label: localize('symbol.details.shares.outstanding'),
      value: sharesOutstandingValue,
      precision: null
    },
    {
      key: 'marketCap',
      label: localize('symbol.details.market.cap'),
      value: marketCapValue,
      precision: null
    },
    {
      key: 'vwap',
      label: localize('symbol.details.vwap'),
      value: vwapValue,
      precision: precision
    }
  ];

  return (
    <div className="summary-section">
      <div className="summary-section-content">
        <div className="market-cap-section-table">
          <div className="market-cap-section-table-body">
            {rows.map((row) => (
              <div className="market-cap-section-table-row" key={row.key}>
                <div className="market-cap-section-table-cell-title">
                  {row.label}
                </div>
                <div className="market-cap-section-table-cell-value">
                  <CssFlash value={row.value} isChange={false}>
                    <Number
                      value={row.value}
                      precision={row.precision}
                      hideIfZero={true}
                    />
                  </CssFlash>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketCapSection;
